import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { areDatesWithoutHoursTheSame, intlFormatDateShort, intlFormatDateWithHour } from "../../../../utils/time-utils";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import { LoadResponse } from "../../../../models/api/load";
import { EmployeeApproversDTO } from "../../../../models/employee-approver/employee-approver-dto";
import { State } from "../TnAEmployeesApprovers";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import ExportToExcelApproversDialog, {
  ApproversOptions,
} from "./ExportToExcelApproversDialog/ExportToExcelApproversDialog";
import { isAfter, isBefore } from "date-fns";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnAEmployeeApproversData: (pageSize?: number) => Promise<LoadResponse<EmployeeApproversDTO> | undefined>;
  state: State;
}

const ExportEmployeesApproversToExcel = ({
  disabled,
  loadEmployeeGroup,
  loadTnAEmployeeApproversData,
  state,
}: Props) => {
  const { t } = useTranslation();
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const tRP = "components.team_presence_and_leaves_approvers";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: [`Employees approvers`],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entity: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
      ];
    },
    [legalEntityOption?.legalEntityIds, selectedLegalEntity?.name, employeeGroupOption]
  );

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t(`common_words.employee`)}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t(`common_words.legal_entity`)}`,
      `${t(`common_words.tna_employee_group`)}`,
      `${t(`${tRP}.fields.presence_and_leaves_approver`)}`,
      `${t(`${tRP}.fields.expense_report_approvers`)}`,
      `${t(`common_words.project`)}`,
      `${t(`common_words.client`)}`,
    ];
  }, []);

  const dataToExportAsExcel = useCallback(
    (allItems: EmployeeApproversDTO[], onlyActualApprovers: boolean = true) => {
      let result: any[] = [];

      const resultItems = onlyActualApprovers
        ? allItems.map((item) => {
            return {
              ...item,
              projectClientApprovers: item.projectClientApprovers.map((projClientApprover) => {
                return {
                  ...projClientApprover,
                  clientApprovers: projClientApprover.clientApprovers.filter((approver) => {
                    return (
                      !(
                        approver.endDate &&
                        isBefore(new Date(approver.endDate), new Date()) &&
                        !areDatesWithoutHoursTheSame(new Date(approver.endDate), new Date())
                      ) &&
                      !(
                        approver.startDate &&
                        isAfter(new Date(approver.startDate), new Date()) &&
                        !areDatesWithoutHoursTheSame(new Date(approver.startDate), new Date())
                      )
                    );
                  }),
                };
              }),
            };
          })
        : allItems;

      resultItems.forEach((item) => {
        if (item.projectClientApprovers.length > 1) {
          item.projectClientApprovers.forEach((projectClientApprover) => {
            result.push({
              [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
              [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
              [`${t(`common_words.employee`)}`]: {
                value: item.employee?.name ?? " ",
                style: { ...excelStyles.employeeCellStyle },
              },
              [`${t(`${tRPCommon}.birth_date`)}`]: {
                value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
              },
              [`${t(`common_words.legal_entity`)}`]: { value: item.employee?.legalEntity?.name ?? " " },
              [`${t(`common_words.tna_employee_group`)}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
              [`${t(`${tRP}.fields.presence_and_leaves_approver`)}`]: {
                value: item.managerApproversTimesheet.length
                  ? item.managerApproversTimesheet.map((approver) => approver.name).join(", ")
                  : "",
                style: { ...excelStyles.managerClientCellStyle },
              },
              [`${t(`${tRP}.fields.expense_report_approvers`)}`]: {
                value: item.managerApproversExpenses.length
                  ? item.managerApproversExpenses.map((approver) => approver.name).join(", ")
                  : "",
                style: { ...excelStyles.managerClientCellStyle },
              },
              [`${t(`common_words.project`)}`]: {
                value: `${projectClientApprover?.project?.name ?? "<NO PROJECT NAME>"} ( ${
                  CustomDate.ToDate(projectClientApprover.start)
                    ? `From ${intlFormatDateShort(CustomDate.ToDate(projectClientApprover.start) as Date)}`
                    : "<NO START DATE>"
                } ${
                  CustomDate.ToDate(projectClientApprover.end)
                    ? ` - To ${intlFormatDateShort(CustomDate.ToDate(projectClientApprover.end) as Date)}`
                    : ""
                } )`,
                style: { ...excelStyles.projectCellStyle },
              },
              [`${t(`common_words.client`)}`]: {
                value: projectClientApprover?.clientApprovers?.length
                  ? projectClientApprover?.clientApprovers
                      ?.map((approver) => `${approver?.user?.name} (${approver?.user?.email})`)
                      .join(", ")
                  : " ",
                style: { ...excelStyles.managerClientCellStyle },
              },
              sortDirection: headerNames,
            });
          });
        } else {
          result.push({
            [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
            [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
            [`${t(`common_words.employee`)}`]: {
              value: item.employee?.name ?? " ",
              style: { ...excelStyles.employeeCellStyle },
            },
            [`${t(`${tRPCommon}.birth_date`)}`]: {
              value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
            },
            [`${t(`common_words.legal_entity`)}`]: { value: item.employee?.legalEntity?.name ?? " " },
            [`${t(`common_words.tna_employee_group`)}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
            [`${t(`${tRP}.fields.presence_and_leaves_approver`)}`]: {
              value: item.managerApproversTimesheet.length
                ? item.managerApproversTimesheet.map((approver) => approver.name).join(", ")
                : "",
              style: { ...excelStyles.managerClientCellStyle },
            },
            [`${t(`${tRP}.fields.expense_report_approvers`)}`]: {
              value: item.managerApproversExpenses.length
                ? item.managerApproversExpenses.map((approver) => approver.name).join(", ")
                : "",
              style: { ...excelStyles.managerClientCellStyle },
            },
            [`${t(`common_words.project`)}`]: {
              value: item.projectClientApprovers.length
                ? `${item.projectClientApprovers[0]?.project?.name} ( ${
                    CustomDate.ToDate(item.projectClientApprovers[0]?.start)
                      ? `From ${intlFormatDateShort(CustomDate.ToDate(item.projectClientApprovers[0]?.start) as Date)}`
                      : "<NO START DATE>"
                  } ${
                    CustomDate.ToDate(item.projectClientApprovers[0]?.end)
                      ? ` - To ${intlFormatDateShort(CustomDate.ToDate(item.projectClientApprovers[0]?.end) as Date)}`
                      : ""
                  } )`
                : "",
              style: { ...excelStyles.projectCellStyle },
            },
            [`${t(`common_words.client`)}`]: {
              value: item.projectClientApprovers.length
                ? item.projectClientApprovers[0]?.clientApprovers
                    ?.map((approver) => `${approver?.user?.name} (${approver?.user?.email})`)
                    .join(", ")
                : "",
              style: { ...excelStyles.managerClientCellStyle },
            },
            sortDirection: headerNames,
          });
        }
      });

      return result;
    },
    [headerNames]
  );

  const exportToExcel = useCallback(
    async (option: ApproversOptions) => {
      if (
        option === ApproversOptions.exportSelectedDataWithAllApprovers ||
        option === ApproversOptions.exportSelectedDataWithActuallApprovers
      ) {
        const employeeGroup = !!employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const approvers =
          option === ApproversOptions.exportSelectedDataWithActuallApprovers
            ? "Actual client approvers"
            : "All client approvers";

        exportTnAToExcel(
          dataToExportAsExcel(state.items, option === ApproversOptions.exportSelectedDataWithActuallApprovers),
          headerNames as string[],
          `${new Date().getFullYear()} ${new Date().getMonth() + 1} ${new Date().getDate()} Employee approvers ${
            !legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""
          } ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } ${approvers}`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (
        option === ApproversOptions.exportAllDataWithAllApprovers ||
        option === ApproversOptions.exportAllDataWithActuallApprovers
      ) {
        const employeeGroup = !!employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const allTnAEmployeesApproversData = await loadTnAEmployeeApproversData(state?.totalItems);

        if (!!allTnAEmployeesApproversData?.error || !allTnAEmployeesApproversData?.items) return;

        const approvers =
          option === ApproversOptions.exportAllDataWithActuallApprovers
            ? "Actual client approvers"
            : "All client approvers";

        exportTnAToExcel(
          dataToExportAsExcel(
            allTnAEmployeesApproversData.items,
            option === ApproversOptions.exportAllDataWithActuallApprovers
          ),
          headerNames as string[],
          `${new Date().getFullYear()} ${new Date().getMonth() + 1} ${new Date().getDate()} Employee approvers ${
            !legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""
          } ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } ${approvers}`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      state.items,
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      loadTnAEmployeeApproversData,
      state?.totalItems,
    ]
  );

  return (
    <ExportToExcelApproversDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportEmployeesApproversToExcel;
